import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Directory from "@/pages/Directory";
import PlayerProfile from "@/pages/PlayerProfile";
import Admin from "@/pages/Admin";
import AdminSettings from "@/pages/AdminSettings";
import AdminScanStats from "@/pages/AdminScanStats";
import AdminImportTournament from "@/pages/AdminImportTournament";
import AdminCoaches from "@/pages/AdminCoaches";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachTeamEdit from "@/pages/CoachTeamEdit";
import TournamentsList from "@/pages/TournamentsList";
import TournamentProfile from "@/pages/TournamentProfile";
import TournamentJoin from "@/pages/TournamentJoin";
import AdminTournament from "@/pages/AdminTournament";
import ChampionshipsList from "@/pages/ChampionshipsList";
import ChampionshipProfile from "@/pages/ChampionshipProfile";
import AdminChampionship from "@/pages/AdminChampionship";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import AdminOrganizers from "@/pages/AdminOrganizers";
import AdminTeams from "@/pages/AdminTeams";
import AdminTeam from "@/pages/AdminTeam";
import TeamProfile from "@/pages/TeamProfile";
import Showcase from "@/pages/Showcase";
import AdminShowcase from "@/pages/AdminShowcase";
import AdminLeads from "@/pages/AdminLeads";
import OrgProfile from "@/pages/OrgProfile";
import AdminOrganizations from "@/pages/AdminOrganizations";
import AdminOrganization from "@/pages/AdminOrganization";
import Login from "@/pages/Login";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminOnlyRoute from "@/components/AdminOnlyRoute";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <Routes>
              {/* Admin-only listing pages (visitors via QR/NFC see VisitorLanding) */}
              <Route
                path="/"
                element={
                  <AdminOnlyRoute>
                    <Directory />
                  </AdminOnlyRoute>
                }
              />
              <Route
                path="/tournaments"
                element={
                  <AdminOnlyRoute>
                    <TournamentsList />
                  </AdminOnlyRoute>
                }
              />
              <Route
                path="/championships"
                element={
                  <AdminOnlyRoute>
                    <ChampionshipsList />
                  </AdminOnlyRoute>
                }
              />

              {/* Public profile pages — accessed via QR/NFC scan */}
              <Route path="/player/:slug" element={<PlayerProfile />} />
              <Route path="/tournament/:slug" element={<TournamentProfile />} />
              <Route path="/championship/:slug" element={<ChampionshipProfile />} />
              <Route path="/association/:slug" element={<OrgProfile type="association" />} />
              <Route path="/entreprise/:slug" element={<OrgProfile type="company" />} />
              <Route
                path="/admin/associations"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminOrganizations type="association" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/associations/:id"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminOrganization type="association" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminOrganizations type="company" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies/:id"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminOrganization type="company" />
                  </ProtectedRoute>
                }
              />
              <Route path="/tournament/join/:code" element={<TournamentJoin />} />
              <Route path="/team/:slug" element={<TeamProfile />} />
              <Route path="/showcase" element={<Showcase />} />
              <Route path="/login" element={<Login />} />

              {/* Coach dashboard (any authenticated user; coaches land here by default) */}
              <Route
                path="/coach"
                element={
                  <ProtectedRoute>
                    <CoachDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizer"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <OrganizerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/team/:id"
                element={
                  <ProtectedRoute>
                    <CoachTeamEdit />
                  </ProtectedRoute>
                }
              />

              {/* Protected admin — strict admin role required */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/scans"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminScanStats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/coaches"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCoaches />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/organizers"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminOrganizers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tournaments/import"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminImportTournament />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tournaments/new"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminTournament />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tournaments/:id"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminTournament />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/championships/new"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminChampionship />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/championships/:id"
                element={
                  <ProtectedRoute roles={["admin", "organizer"]}>
                    <AdminChampionship />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/teams"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminTeams />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/teams/new"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminTeam />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/teams/:id"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminTeam />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/leads"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLeads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/showcase"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminShowcase />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/:id"
                element={
                  <ProtectedRoute requireAdmin>
                    <Admin />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" />
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
